import { domToBlob } from "modern-screenshot";

const CAPTURE_PADDING_PX = 20;
const CAPTURE_GAP_PX = 12;
const CAPTURE_SCALE = 3;

/**
 * Resolve a solid page/pane background for the padded frame around the bubble.
 * @returns {string}
 */
function resolveCaptureFrameBackground() {
  const root = document.documentElement;
  const styles = getComputedStyle(root);
  for (const name of ["--chat-pane-bg", "--background", "--card"]) {
    const raw = styles.getPropertyValue(name).trim();
    if (raw) {
      return raw;
    }
  }
  const bodyBg = getComputedStyle(document.body).backgroundColor;
  if (bodyBg && bodyBg !== "rgba(0, 0, 0, 0)" && bodyBg !== "transparent") {
    return bodyBg;
  }
  return "#0f1117";
}

/**
 * @param {HTMLElement} el
 * @returns {HTMLElement}
 */
function cloneBubbleForCapture(el) {
  const rect = el.getBoundingClientRect();
  const width = Math.max(1, Math.ceil(rect.width));
  const clone = /** @type {HTMLElement} */ (el.cloneNode(true));
  clone.querySelectorAll("[data-capture-ignore]").forEach((node) => node.remove());
  clone.style.width = `${width}px`;
  clone.style.maxWidth = `${width}px`;
  clone.style.minWidth = `${width}px`;
  clone.style.height = "auto";
  clone.style.margin = "0";
  clone.style.transform = "none";
  clone.style.position = "relative";
  clone.style.left = "auto";
  clone.style.right = "auto";
  clone.style.top = "auto";
  clone.style.bottom = "auto";
  clone.style.overflow = "hidden";
  clone.style.boxShadow = "none";
  return clone;
}

/**
 * Build an off-screen, chrome-free stack of message bubbles for rasterization.
 *
 * @param {HTMLElement[]} els
 * @param {string} frameBackground
 * @returns {{ host: HTMLElement, target: HTMLElement }}
 */
function mountCaptureCloneStack(els, frameBackground) {
  const host = document.createElement("div");
  host.setAttribute("aria-hidden", "true");
  host.dataset.captureIgnore = "true";
  host.style.cssText = [
    "position:fixed",
    "left:-100000px",
    "top:0",
    "z-index:-1",
    "pointer-events:none",
    "margin:0",
    "padding:0",
  ].join(";");

  const frame = document.createElement("div");
  frame.style.cssText = [
    `padding:${CAPTURE_PADDING_PX}px`,
    `background:${frameBackground}`,
    "display:flex",
    "flex-direction:column",
    `gap:${CAPTURE_GAP_PX}px`,
    "align-items:flex-start",
    "box-sizing:border-box",
    "margin:0",
    "line-height:normal",
    "width:max-content",
    "max-width:920px",
  ].join(";");

  for (const el of els) {
    frame.appendChild(cloneBubbleForCapture(el));
  }

  host.appendChild(frame);
  document.body.appendChild(host);
  return { host, target: frame };
}

/**
 * Capture one or more DOM nodes (message bubble `<article>`s) to a crisp PNG blob.
 * Multiple nodes are stacked top→bottom in document order.
 *
 * @param {HTMLElement | HTMLElement[]} elOrEls
 * @param {{ backgroundColor?: string }} [options]
 * @returns {Promise<Blob>}
 */
export async function captureElementAsPngBlob(elOrEls, options = {}) {
  const els = (Array.isArray(elOrEls) ? elOrEls : [elOrEls]).filter(Boolean);
  if (!els.length) {
    throw new Error("Nothing to capture");
  }

  const frameBackground = options.backgroundColor || resolveCaptureFrameBackground();
  const { host, target } = mountCaptureCloneStack(els, frameBackground);

  try {
    await new Promise((resolve) => requestAnimationFrame(() => requestAnimationFrame(resolve)));

    const blob = await domToBlob(target, {
      scale: CAPTURE_SCALE,
      dpi: 96 * CAPTURE_SCALE,
      type: "image/png",
      backgroundColor: frameBackground,
      filter: (node) => {
        if (!(node instanceof Element)) return true;
        if (node instanceof HTMLElement && node.dataset?.captureIgnore === "true") return false;
        return true;
      },
    });

    if (!blob) {
      throw new Error("Capture produced an empty image");
    }
    return blob;
  } finally {
    host.remove();
  }
}
